import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import { AnimatePresence, animate, m, useDragControls, useMotionValue } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ITEM_EXIT } from '@/constants/animation';
import { formatCurrency } from '../../constants/i18n';
import { calculateOptimisticLinePrices } from '@/utils/cartCalculations';
import { CartQuantityControl } from './CartQuantityControl';
import { useCartLineItem } from '../../hooks/cart/useCartLineItem';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { emitLineItemSwipeOpen } from '@/events/cartLineItemEvents';
import { emitToast } from '../../events/error/errorEventBus';
import { t } from '../../constants/i18n';

interface CartLineItemProps {
    lineRef: string;
    isOpen: boolean;
}

const SWIPE_ACTION_WIDTH = 120;
const REVEAL_DELETE_WIDTH = SWIPE_ACTION_WIDTH;
const INTENT_SLOP_PX = 12;
const SNAP_THRESHOLD = 40;
const VELOCITY_THRESHOLD = -400;
const DRAG_BOUNDS = { left: -SWIPE_ACTION_WIDTH, right: 0 };

export const CartLineItem: React.FC<CartLineItemProps> = ({ lineRef, isOpen }) => {
    const { item, currency, onRemove, updatingType } = useCartLineItem(lineRef);
    const [optimisticQty, setOptimisticQty] = useState(item?.quantity ?? 0);
    const [cancelPendingUpdate, setCancelPendingUpdate] = useState<(() => void) | null>(null);
    const [prepareForRemove, setPrepareForRemove] = useState<(() => void) | null>(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const isDeletingRef = useRef(false);
    const [isDeleteActionOpen, setIsDeleteActionOpen] = useState(false);
    const isDesktop = useIsDesktop();
    const dragControls = useDragControls();
    const x = useMotionValue(0);
    const autoCloseTimerRef = useRef<number | null>(null);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const deleteAreaRef = useRef<HTMLDivElement | null>(null);
    const intentRef = useRef({
        startX: 0,
        startY: 0,
        active: false,
        decided: false
    });

    useEffect(() => {
        if (item) {
            setOptimisticQty(item.quantity);
        }
    }, [item]);

    const animateTo = useCallback((targetX: number) => {
        animate(x, targetX, {
            type: 'spring',
            stiffness: 300,
            damping: 30
        });
    }, [x]);

    const animateReveal = useCallback((targetX: number) => {
        animate(x, targetX, {
            type: 'tween',
            duration: 0.5,
            ease: [0.16, 1, 0.3, 1]
        });
    }, [x]);

    const closeSwipePosition = useCallback(() => {
        animateTo(0);
    }, [animateTo]);

    const emitOpenLineItem = useCallback(() => {
        emitLineItemSwipeOpen(lineRef);
    }, [lineRef]);

    const emitCloseLineItem = useCallback(() => {
        emitLineItemSwipeOpen(null);
    }, []);

    const clearAutoCloseTimer = useCallback(() => {
        if (autoCloseTimerRef.current !== null) {
            window.clearTimeout(autoCloseTimerRef.current);
            autoCloseTimerRef.current = null;
        }
    }, []);

    const closeDeleteAction = useCallback((notifyClose = false) => {
        if (isDeletingRef.current) return;
        clearAutoCloseTimer();
        setIsDeleteActionOpen(false);
        if (!isDesktop) {
            animateReveal(0);
        }
        if (notifyClose) {
            emitCloseLineItem();
        }
    }, [animateReveal, clearAutoCloseTimer, isDesktop, emitCloseLineItem]);

    const openDeleteAction = useCallback((autoResetMs?: number) => {
        if (isDeletingRef.current || isDesktop) return;
        emitOpenLineItem();
        clearAutoCloseTimer();
        setIsDeleteActionOpen(true);
        if (!isDesktop) {
            animateReveal(-REVEAL_DELETE_WIDTH);
        }
        if (autoResetMs) {
            autoCloseTimerRef.current = window.setTimeout(() => {
                closeDeleteAction(true);
            }, autoResetMs);
        }
    }, [animateReveal, clearAutoCloseTimer, isDesktop, emitOpenLineItem, closeDeleteAction]);

    const onMinAttempt = useCallback(() => {
        openDeleteAction(3500);
    }, [openDeleteAction]);

    const setDeleting = useCallback((next: boolean) => {
        isDeletingRef.current = next;
        setIsDeleting(next);
    }, []);

    const handleRemove = async () => {
        setDeleting(true);
        console.log('[CartLineItem] handleRemove', { lineRef });
        cancelPendingUpdate?.();
        prepareForRemove?.();
        try {
            const result = await onRemove();
            if (!result?.ok) {
                emitToast({
                    type: 'error',
                    message: result?.error?.message ?? t('cart.errors.updateFailed')
                });
                setDeleting(false);
                emitCloseLineItem();
                closeDeleteAction(true);
                return;
            }
        } catch {
            emitToast({
                type: 'error',
                message: t('cart.errors.updateFailed')
            });
            setDeleting(false);
            emitCloseLineItem();
            closeDeleteAction(true);
            return;
        }
    };

    const confirmDelete = useCallback(async () => {
        clearAutoCloseTimer();
        await handleRemove();
    }, [clearAutoCloseTimer, handleRemove]);

    useEffect(() => {
        if (!isDesktop) return;
        if (isDeletingRef.current) return;
        if (isDeleteActionOpen) {
            closeDeleteAction(true);
            return;
        }
        closeSwipePosition();
        if (isOpen) {
            emitCloseLineItem();
        }
    }, [closeDeleteAction, closeSwipePosition, emitCloseLineItem, isDeleteActionOpen, isDesktop, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            if (isDeleteActionOpen) {
                closeDeleteAction();
            } else {
                closeSwipePosition();
            }
        }
    }, [closeDeleteAction, closeSwipePosition, isDeleteActionOpen, isOpen]);

    useEffect(() => {
        if (isDesktop || !isDeleteActionOpen) return;
        const handler = (event: PointerEvent) => {
            const target = event.target as Node | null;
            if (!target) return;
            if (deleteAreaRef.current?.contains(target)) return;
            if (rootRef.current?.contains(target)) {
                event.preventDefault();
                event.stopPropagation();
                closeDeleteAction(true);
                return;
            }
            closeDeleteAction(true);
        };
        document.addEventListener('pointerdown', handler, true);
        return () => document.removeEventListener('pointerdown', handler, true);
    }, [closeDeleteAction, isDeleteActionOpen, isDesktop]);

    useEffect(() => {
        return () => {
            clearAutoCloseTimer();
        };
    }, [clearAutoCloseTimer]);

    if (!item) return null;

    const isRemoving = updatingType === 'remove' || isDeleting;
    const { finalLinePrice, originalLinePrice, hasDiscount } = calculateOptimisticLinePrices(item, optimisticQty);

    const desktopRevealStyle = isDesktop
        ? {
            transform: `translateX(${isDeleteActionOpen ? -REVEAL_DELETE_WIDTH : 0}px)`,
            transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1)',
            boxShadow: isDeleteActionOpen ? 'inset -8px 0 12px rgba(0,0,0,0.08)' : undefined
        }
        : undefined;

    return (
        <>
            <div ref={rootRef} className="relative overflow-hidden bg-white">
                <AnimatePresence>
                    {isDeleteActionOpen && (
                        <m.div
                        ref={deleteAreaRef}
                        className="absolute inset-y-0 right-0 z-0 flex items-center justify-center"
                        style={{
                            width: isDesktop ? REVEAL_DELETE_WIDTH : SWIPE_ACTION_WIDTH,
                            backgroundColor: '#E53935'
                        }}
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 12 }}
                        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <button
                            type="button"
                            className="h-full w-full flex items-center justify-center gap-2 text-white text-sm font-semibold tracking-wide"
                            onPointerDown={() => prepareForRemove?.()}
                            onClick={confirmDelete}
                            disabled={isRemoving}
                            aria-label="Remove item"
                        >
                            {isRemoving ? (
                                <Loader2 className="h-[26px] w-[26px] text-white animate-spin" />
                            ) : (
                                <Trash2 className="h-5 w-5 text-white" />
                            )}
                        </button>
                        </m.div>
                    )}
                </AnimatePresence>

                <m.div
                    variants={ITEM_EXIT}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    drag={isDesktop || isDeleteActionOpen ? false : 'x'}
                    dragControls={dragControls}
                    dragListener={false}
                    dragConstraints={DRAG_BOUNDS}
                    dragElastic={{ left: 0.2, right: 0 }}
                    dragMomentum={false}
                    style={isDesktop ? desktopRevealStyle : { x }}
                    onPointerDown={(e) => {
                        if (isDesktop) return;
                        emitOpenLineItem();
                        intentRef.current = {
                            startX: e.clientX,
                            startY: e.clientY,
                            active: true,
                            decided: false
                        };
                    }}
                    onPointerMove={(e) => {
                        if (isDesktop) return;
                        const state = intentRef.current;
                        if (!state.active || state.decided) return;
                        const dx = e.clientX - state.startX;
                        const dy = e.clientY - state.startY;
                        if (Math.abs(dx) < INTENT_SLOP_PX && Math.abs(dy) < INTENT_SLOP_PX) return;
                        state.decided = true;
                        if (Math.abs(dx) > Math.abs(dy)) {
                            dragControls.start(e);
                        }
                    }}
                    onPointerUp={() => {
                        intentRef.current.active = false;
                        intentRef.current.decided = false;
                    }}
                    onPointerCancel={() => {
                        intentRef.current.active = false;
                        intentRef.current.decided = false;
                    }}
                    onDragEnd={(_, info) => {
                        if (isDesktop) return;
                        const finalX = x.get();
                        if (finalX < 0 && (Math.abs(finalX) > SNAP_THRESHOLD || info.velocity.x < VELOCITY_THRESHOLD)) {
                            openDeleteAction();
                        } else {
                            closeSwipePosition();
                        }
                    }}
                    // 1. 修改：移除了 'items-center'。
                    // 默认 flex-row 会 stretch 高度，这使得右侧容器的高度会自动跟随左侧图片的高度（或者反之，取较高者）。
                    // 这样 justify-between 才能把内容推到最上和最下。
                    className={`relative z-10 flex gap-5 py-5 pr-4 sm:pr-0 bg-white group ${isRemoving ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}
                >
                    {/* 图片区域 */}
                    <div className="relative h-20 w-20 sm:h-28 sm:w-28 shrink-0 overflow-hidden rounded-xl">
                        {item.image ? (
                            <img
                                src={item.image}
                                alt={item.product_title}
                                draggable={false}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300 bg-gray-50 text-xs">No Image</div>
                        )}
                    </div>

                    {/* 内容区域 */}
                    {/* 2. 保持：flex-col + justify-between 确保上下两端对齐 */}
                    <div className="flex-1 flex flex-col justify-between min-w-0">
                        
                        {/* 上部分：标题 & 选项 */}
                        {/* 3. 修改：移除了 mt-2，确保物理顶对齐 */}
                        <div>
                            <div className="flex justify-between items-start gap-1">
                                <h3 className="text-base font-semibold text-gray-900 leading-snug line-clamp-2 select-text">
                                    <a href={item.url} className="hover:text-gray-600 transition-colors">
                                        {item.product_title}
                                    </a>
                                </h3>
                            </div>

                            {item.variant_title && (
                                // 4. 修改：mt-1 改为 mt-2 (Tailwind 的 mt-2 通常是 0.5rem = 8px)，
                                // 实现了你要求的“和标题 8px 边距”。
                                <p className="mt-2 text-sm text-gray-500 select-text">{item.variant_title}</p>
                            )}
                        </div>

                        {/* 下部分：数量选择器 & 价格 */}
                        {/* 5. 修改：移除了 mt-2，靠父容器的 justify-between 自动推到底部 */}
                        <div className="flex items-end justify-between">
                            {/* 数量选择器 + 删除按钮 */}
                            <div className="flex items-center">
                                <CartQuantityControl
                                    lineRef={lineRef}
                                    quantity={item.quantity}
                                    onOptimisticQtyChange={setOptimisticQty}
                                    onRegisterCancel={setCancelPendingUpdate}
                                    onRegisterPrepareForRemove={setPrepareForRemove}
                                    onMinAttempt={onMinAttempt}
                                />

                                {isDesktop && (
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-10 w-10 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors shrink-0 ml-1"
                                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                        onMouseDown={() => prepareForRemove?.()}
                                        onClick={handleRemove}
                                        disabled={isRemoving}
                                        aria-label="Remove item"
                                    >
                                        {isRemoving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                                    </Button>
                                )}
                            </div>

                            {/* 价格显示 */}
                            <div className="text-right flex flex-col justify-end">
                                {hasDiscount && (
                                    <span className="text-xs text-gray-400 line-through mb-0.5 select-text">
                                        {formatCurrency(originalLinePrice, currency)}
                                    </span>
                                )}
                                <span className={`text-base font-bold tracking-tight ${hasDiscount ? 'text-red-600' : 'text-gray-900'} select-text`}>
                                    {formatCurrency(finalLinePrice, currency)}
                                </span>
                            </div>
                        </div>
                    </div>
                </m.div>
            </div>
            <Separator className="bg-gray-100" />
        </>
    );
};
